import * as React from 'react'
import * as T from 'components/ui/typography'
import * as firebaseSdk from 'sdk/firebase'
import * as TestySDK from 'sdk/testy-web-sdk'
import * as AuthUtils from 'utils/auth/auth'

import { useRouter } from 'next/router'
import { ConfirmationResult } from 'firebase/auth'

import Button from 'components/shared/button'
import FormTextField from 'components/shared/form-field-input'
import AppLogo from 'components/shared/logo'

import Routes from 'utils/routes'
import { AuthContext } from 'providers/auth-provider'
import { useFormik } from 'formik'
import { OTPschema } from 'utils/form/validation-schema'
import LinkButton from 'components/shared/link-button'
import { CheckCircle2 } from 'lucide-react'

export default function VerifyPhoneCode({ phone, name }: { phone: string; name: string }) {
    const router = useRouter()

    const [loading, setLoading] = React.useState(false)
    const { setUser } = React.useContext(AuthContext)

    const [phoneConfirmationResult, setPhoneConfirmationResult] = React.useState<ConfirmationResult | undefined>()
    const [phoneOTPCodeHelperText, setPhoneOTPCodeHelperText] = React.useState('')

    const form = useFormik({
        initialValues: {
            phoneOTPCode: '',
        },
        onSubmit: () => {
            onSubmit()
        },
        validationSchema: OTPschema,
    })

    const signUpWithPhone = async () => {
        if (!phoneConfirmationResult) throw new Error('Phone confirmation result is undefined')

        firebaseSdk
            .checkPhoneVerificationCode(form.values.phoneOTPCode, phoneConfirmationResult)
            .then(async (user) => {
                const idToken = await user.user.getIdToken()
                const token = await AuthUtils.exchangeFirebaseTokenWithCustom(idToken)
                const userBasicData = AuthUtils.userBasicDataParser(user.user, token)
                TestySDK.setAuthToken(userBasicData.token)

                setUser(userBasicData)
                router.push(Routes.Home)
            })
            .catch((e) => {
                setPhoneOTPCodeHelperText(e.message)
            })
            .finally(async () => {
                await TestySDK.createUser({
                    phone_number: phone,
                    name: name,
                })
                setLoading(false)
            })
    }

    const sendPhoneOTPCode = React.useCallback(async () => {
        firebaseSdk.signWithPhoneNumber(phone).then((res) => {
            setPhoneConfirmationResult(res)
        })
    }, [phone])

    const onSubmit = async () => {
        setPhoneOTPCodeHelperText('')
        setLoading(true)
        await signUpWithPhone()
    }

    React.useEffect(() => {
        sendPhoneOTPCode()
    }, [sendPhoneOTPCode])

    return (
        <div>
            <AppLogo />

            <T.H2 className="my-3 text-start">Ci sei quasi</T.H2>

            <T.Muted className="text-start mb-3">Completa l&apos;account per accedere</T.Muted>

            {/** Phone OTP field */}
            <FormTextField
                value={form.values.phoneOTPCode}
                error={form.errors.phoneOTPCode ?? phoneOTPCodeHelperText}
                onChangeFn={form.handleChange('phoneOTPCode')}
                placeholder="Codice OTP"
            />
            <div id="recaptcha-container"></div>

            {/** Send new OTP */}
            <div className="flex items-center justify-between mt-3 mb-2">
                <div className="flex items-center">
                    <CheckCircle2 className="fill-primary text-white" />
                    <T.Small className="text-primary">Non hai ricevuto il codice?</T.Small>
                </div>
                <LinkButton dataCy="resend otp" onClick={() => sendPhoneOTPCode()}>
                    <p className="ml-1">Invia di nuovo</p>
                </LinkButton>
            </div>

            <Button className="w-full mt-3" disabled={!form.isValid} loading={loading} onClick={onSubmit}>
                Continua
            </Button>
        </div>
    )
}
